import React from 'react';
import { XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from 'recharts';
import Icon from '../../../components/AppIcon';
import { clientPortalService } from '../../../services/clientPortalService';

const TrendAnalysis = ({ progressData, assessments }) => {
  const dimensions = [
    { key: 'data_infrastructure', label: 'Data Infrastructure', color: '#3B82F6' },
    { key: 'analytics_capabilities', label: 'Analytics Capabilities', color: '#10B981' },
    { key: 'governance_maturity', label: 'Governance Maturity', color: '#8B5CF6' },
    { key: 'team_skills', label: 'Team Skills', color: '#F59E0B' },
    { key: 'strategic_alignment', label: 'Strategic Alignment', color: '#EF4444' }
  ];

  const timeSeriesData = clientPortalService?.getTimeSeriesProgressData(progressData);

  // Calculate trend statistics
  const trendStats = dimensions?.map(dimension => {
    const dimensionData = progressData?.filter(p => p?.dimension === dimension?.key)?.sort((a, b) => 
      new Date(a?.trackedAt) - new Date(b?.trackedAt)
    );

    if (dimensionData?.length < 2) {
      return {
        dimension: dimension?.label,
        trend: 'stable',
        change: 0,
        color: dimension?.color
      };
    }

    const oldest = dimensionData?.[0];
    const latest = dimensionData?.[dimensionData?.length - 1];
    const change = latest?.score - oldest?.score;
    const changePercentage = ((change / oldest?.score) * 100)?.toFixed(1);

    return {
      dimension: dimension?.label,
      trend: change > 0 ? 'up' : change < 0 ? 'down' : 'stable',
      change,
      changePercentage,
      oldestScore: oldest?.score,
      latestScore: latest?.score,
      color: dimension?.color
    };
  });

  // Calculate average improvement rate
  const totalChange = trendStats?.reduce((sum, stat) => sum + stat?.change, 0);
  const averageImprovement = (totalChange / trendStats?.length)?.toFixed(1);

  if (!progressData || progressData?.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-elevation-1 p-8 mb-8">
        <div className="text-center py-12">
          <Icon name="TrendingUp" size={48} className="text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No trend data available</p>
          <p className="text-sm text-gray-400 mt-2">Complete multiple assessments to see trend analysis</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-elevation-1 p-6 mb-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
          <Icon name="TrendingUp" size={20} className="text-green-600" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-[#002B45]">Trend Analysis</h3>
          <p className="text-sm text-gray-500">Maturity progression across all five dimensions</p>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-gradient-to-br from-[#1FB5A5]/10 to-[#1FB5A5]/5 rounded-lg p-4 border border-[#1FB5A5]/20">
          <p className="text-sm text-gray-600 mb-1">Average Improvement</p>
          <div className="flex items-center gap-2">
            <Icon name="TrendingUp" size={24} className="text-[#1FB5A5]" />
            <span className="text-3xl font-bold text-[#1FB5A5]">+{averageImprovement}</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">Points across all dimensions</p>
        </div>
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <p className="text-sm text-gray-600 mb-1">Total Assessments</p>
          <div className="flex items-center gap-2">
            <Icon name="FileText" size={24} className="text-blue-600" />
            <span className="text-3xl font-bold text-blue-600">{assessments?.length}</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">Completed evaluations</p>
        </div>
        <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
          <p className="text-sm text-gray-600 mb-1">Progress Entries</p>
          <div className="flex items-center gap-2">
            <Icon name="BarChart3" size={24} className="text-purple-600" />
            <span className="text-3xl font-bold text-purple-600">{progressData?.length}</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">Data points tracked</p>
        </div>
      </div>

      {/* Trend Chart */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <h4 className="text-sm font-semibold text-[#002B45] mb-4">Maturity Progression Over Time</h4>
        <ResponsiveContainer width="100%" height={350}>
          <AreaChart data={timeSeriesData}>
            <defs>
              {dimensions?.map((dimension) => (
                <linearGradient key={dimension?.key} id={`color${dimension?.key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={dimension?.color} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={dimension?.color} stopOpacity={0} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" tick={{ fontSize: 12 }} />
            <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
            <Tooltip
              content={({ active, payload }) => {
                if (active && payload && payload?.length) {
                  return (
                    <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
                      <p className="font-semibold text-[#002B45] mb-2">{payload?.[0]?.payload?.date}</p>
                      {payload?.map((entry, index) => (
                        <p key={index} className="text-sm" style={{ color: entry?.color }}>
                          {dimensions?.find(d => d?.key === entry?.dataKey)?.label}: {entry?.value}
                        </p>
                      ))}
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend wrapperStyle={{ fontSize: '12px' }} />
            {dimensions?.map((dimension) => (
              <Area
                key={dimension?.key}
                type="monotone"
                dataKey={dimension?.key}
                stroke={dimension?.color}
                strokeWidth={2}
                fill={`url(#color${dimension?.key})`}
                name={dimension?.label?.split(' ')?.[0]}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Dimension Trends */}
      <div className="pt-6 border-t border-gray-200">
        <h4 className="text-sm font-semibold text-[#002B45] mb-4">Dimension-Specific Trends</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {trendStats?.map((stat, index) => (
            <div
              key={index}
              className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:shadow-md transition-shadow duration-300"
            >
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-semibold text-gray-700">{stat?.dimension}</p>
                <Icon
                  name={stat?.trend === 'up' ? 'TrendingUp' : stat?.trend === 'down' ? 'TrendingDown' : 'Minus'}
                  size={20}
                  className={stat?.trend === 'up' ? 'text-green-600' : stat?.trend === 'down' ? 'text-red-600' : 'text-gray-600'}
                />
              </div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-500">Start:</span>
                <span className="text-lg font-bold text-gray-700">{stat?.oldestScore}</span>
              </div>
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs text-gray-500">Current:</span>
                <span className="text-lg font-bold" style={{ color: stat?.color }}>{stat?.latestScore}</span>
              </div>
              <div className="pt-3 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-600">Total Change:</span>
                  <span className={`text-sm font-semibold ${
                    stat?.trend === 'up' ? 'text-green-600' : stat?.trend === 'down' ? 'text-red-600' : 'text-gray-600'
                  }`}>
                    {stat?.change > 0 ? '+' : ''}{stat?.change} ({stat?.changePercentage > 0 ? '+' : ''}{stat?.changePercentage}%)
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TrendAnalysis;